import React, { useEffect, useState } from 'react';
import '../App.css';
import { Icon, InlineIcon } from '../components/Icon';
import { normalizeDocRef } from '../storage';
import { downloadDocument } from './documentActions';
import { DocumentPdfView } from './DocumentPdfView';
import { resolveMobileViewBlobUrl } from './documents';

function isImageFile(name = '') {
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
}

function isPdfFile(name = '') {
  return /\.pdf$/i.test(name);
}

function ViewDocumentModal({ data, onClose }) {
  const ref = normalizeDocRef(data?.doc, data?.name);
  const title = ref?.name || data?.name || 'Document';
  const viewCtx = {
    storagePath: data?.storagePath,
    listPrefix: data?.listPrefix,
    companyId: data?.companyId,
    taskId: data?.taskId,
    dirIdx: data?.dirIdx,
    slotId: data?.slotId,
    files: data?.files,
  };
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isImage = isImageFile(title) || isImageFile(ref?.path) || isImageFile(ref?.name);
  const isPdf = isPdfFile(title) || isPdfFile(ref?.path) || isPdfFile(ref?.name);

  useEffect(() => {
    let alive = true;
    let objectUrl = null;

    (async () => {
      setLoading(true);
      setError('');
      setBlobUrl(null);
      try {
        objectUrl = await resolveMobileViewBlobUrl(ref, data?.name, viewCtx);
        if (alive) setBlobUrl(objectUrl);
      } catch (e) {
        if (alive) setError(e.message || 'Could not open document');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    data?.storagePath,
    data?.listPrefix,
    data?.companyId,
    data?.taskId,
    data?.dirIdx,
    data?.slotId,
    ref?.path,
    ref?.url,
    ref?.name,
    title,
  ]);

  const onDownload = async () => {
    try {
      await downloadDocument(ref, title, viewCtx);
    } catch (e) {
      setError(e.message || 'Could not download document');
    }
  };

  const canDownload = blobUrl && !error;

  return (
    <div className="modal-box invoice-view-modal document-view-modal" style={{ maxWidth: 820 }}>
      <div className="modal-head">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="modal-title" style={{ wordBreak: 'break-word' }}>{title}</div>
        </div>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
          <Icon name="close" size={16} />
        </button>
      </div>

      <div className="modal-body document-view-body">
        {loading && (
          <div className="empty" style={{ padding: 32 }}>
            <div>Loading document…</div>
          </div>
        )}
        {!loading && error && (
          <div className="empty" style={{ padding: 32 }}>
            <div style={{ color: 'var(--red)', fontWeight: 600 }}>{error}</div>
          </div>
        )}
        {!loading && blobUrl && isImage && (
          <img className="document-view-image" src={blobUrl} alt={title} />
        )}
        {!loading && blobUrl && isPdf && (
          <DocumentPdfView fileUrl={blobUrl} title={title} />
        )}
        {!loading && blobUrl && !isImage && !isPdf && (
          <iframe className="document-view-frame" title={title} src={blobUrl} />
        )}
      </div>

      <div className="modal-foot">
        <button type="button" className="btn" onClick={onClose}>Close</button>
        {canDownload && (
          <button type="button" className="btn btn-primary" onClick={onDownload}>
            <InlineIcon name="download" size={13} gap={5}>Download</InlineIcon>
          </button>
        )}
      </div>
    </div>
  );
}

export { ViewDocumentModal };
